import { Schema } from "effect";
import { CurriculumLanguage } from "./curriculum-tags.ts";

// ADR-0111 — "is this language pack complete enough to generate against, and if not, what exactly
// is missing?"
//
// One pure function, here in `core`, because the same answer is needed in three places that must
// never disagree: the upload form (before anything is written), the "Language taught" select in the
// New Curriculum modal (an L2 whose pack is incomplete must not be offered), and the server's own
// refusal to accept an upload. A second copy of these rules in the web app would drift the moment
// the pipeline's input check changes.
//
// The rules are derived from what actually fails a run, NOT from the prose in
// `L2__TEMPLATE__/README.md`:
//
//   * BLOCKER = `l2_unit_plan/scripts/run_pipeline.py::check_inputs` calls `sys.exit` on it. That is
//     the per-file existence list (`PERSONAS`, `REFERENCE_DIALOGUES`, `LANGUAGE_PROFILE` from the L2
//     pack; `L1_PROFILE`, `TUTORS` from the L1 pack) plus the registry, which both runtimes hard-exit
//     without. A blocker means the launch dies before a cluster does any work.
//   * WARNING = the run starts and degrades, usually silently. The sharpest of these is
//     `language_rules.py`: with `function_words.yml` and friends absent it falls back to SPANISH
//     defaults rather than failing, so an Italian pack missing them produces plausible-looking
//     Spanish-shaped output. Exactly the class of failure a warning has to be loud about.
//   * INFO = something this validator cannot decide and a human should know about, rather than
//     silence that reads as approval. The cast-name gate is the one real case: `validate_personas_md`
//     resolves every `## <Name>` against `persona-config`, whose registry is Python source
//     (`persona_config/_registry.py`), not data — so it is checked at launch, not here.
//
// Deliberately NOT a blocker: the vocab `.zip` and the grammar ledgers. The zip is binary and is not
// even a valid `LanguagePackPath`, and the ledgers are pipeline OUTPUT (step 0 generates them into
// `l2_unit_plan/generated/`), so requiring either at upload time would make every honest pack invalid.

export const PackFindingSeverity = Schema.Literal("blocker", "warning", "info");
export type PackFindingSeverity = typeof PackFindingSeverity.Type;

export const PackFinding = Schema.Struct({
  severity: PackFindingSeverity,
  // The pack-relative path the finding is about (`L2_FRA/personas.md`), or the registry, or absent
  // when the finding is about the upload as a whole.
  path: Schema.optional(Schema.String),
  // What is wrong, stated as a fact.
  message: Schema.String,
  // What to do about it. Every finding carries one — a report that says only "personas.md is
  // missing" makes the reader go and find the template themselves, which is the work this is
  // supposed to remove.
  fix: Schema.String,
});
export type PackFinding = typeof PackFinding.Type;

/** One file of an upload: its pack-relative path and its text. */
export const PackFile = Schema.Struct({
  path: Schema.String,
  content: Schema.String,
});
export type PackFile = typeof PackFile.Type;

export const PackReport = Schema.Struct({
  l2: Schema.String,
  l1: Schema.optional(Schema.String),
  findings: Schema.Array(PackFinding),
  // `true` when nothing is a blocker — the pack may be written, and a run may be launched against
  // it. Warnings never gate: the standing rule from the generation working session is that only
  // checks whose failure produces an unusable result block, everything else informs.
  complete: Schema.Boolean,
});
export type PackReport = typeof PackReport.Type;

const hasBlocker = (findings: readonly PackFinding[]): boolean =>
  findings.some((finding) => finding.severity === "blocker");

// `<!-- pronoun-noise: i, you, he, … -->` in the L1 profile. Absent, the match-exercise parser in
// `convert_unit_plan_md_to_json.py` silently falls back to the ENGLISH pronoun set — so this matters
// for any L1 that is not English, and is harmless for one that is. It is reported at one severity
// regardless, because this validator does not know which L1 is which.
const PRONOUN_NOISE_MARKER = /<!--\s*pronoun-noise:/;

// `dialog_polish.py` parses `personas.md` by H2, and excludes exactly these two headings from the
// persona set BY NAME. Rename either and its content is parsed as a persona named "Learner
// positioning" — which then fails the cast gate at launch with a confusing message.
const REQUIRED_PERSONA_SECTIONS = ["## Learner positioning", "## Cast distribution rules"] as const;

// The six bold fields the template requires of every persona block. Missing ones do not fail a
// launch, they flatten the generated dialogue — the pack is structurally fine and the output is
// generic, which is the failure mode hardest to spot after the fact.
const PERSONA_FIELDS = [
  "**Age / origin:**",
  "**Role:**",
  "**Register with learner:**",
  "**Voice / quirks:**",
  "**Regional / dialect features:**",
  "**Default scene contexts",
] as const;

/** The token every unfilled slot carries, so `checkLanguagePack` can find the ones left behind. */
export const FILL_MARKER = "<<FILL:";

// The marker `validate_tutors_md` keys on when it collects tutor names.
const TUTOR_VOICE_MARKER = "**Voice / tone:**";

const h2Headings = (content: string): readonly string[] =>
  content
    .split("\n")
    .filter((line) => line.startsWith("## "))
    .map((line) => line.trim());

/** The `## <Name>` blocks that are cast entries — everything but the two reserved section titles. */
const castNames = (content: string): readonly string[] =>
  h2Headings(content)
    .filter((heading) => !(REQUIRED_PERSONA_SECTIONS as readonly string[]).includes(heading))
    .map((heading) => heading.slice(3).trim());

/** Split a file on its `## ` headings, so a per-persona check sees only that persona's block. */
const blocksOf = (content: string): readonly { readonly name: string; readonly body: string }[] => {
  const blocks: { name: string; body: string }[] = [];
  for (const line of content.split("\n")) {
    if (line.startsWith("## ")) {
      blocks.push({ name: line.slice(3).trim(), body: "" });
      continue;
    }
    const current = blocks.at(-1);
    if (current !== undefined) current.body += `${line}\n`;
  }
  return blocks;
};

const registryHasCode = (registry: string, code: string): boolean =>
  registry.split("\n").some((line) => line.trim().startsWith(`${code}:`));

// The five per-language YAML lists the Python runtime reads as separate files. The TypeScript
// runtime parses the same lists out of HTML markers inside `language_profile.md` instead, so a pack
// can satisfy one runtime and quietly mis-serve the other.
const LANGUAGE_RULE_FILES = [
  "function_words.yml",
  "country_names.yml",
  "speaker_prelude_verbs.yml",
  "gender_pair_exclude.yml",
  "decisive_orthography.yml",
] as const;

export interface PackCompletenessInput {
  /** The L2 code WITHOUT its `L2_` prefix, e.g. `FRA`. */
  readonly l2: string;
  /** The L1 code without its `L1_` prefix. Omitted when only the L2 side is being judged. */
  readonly l1?: string | undefined;
  /**
   * Every pack file that WILL exist once this upload lands: the uploaded files merged over whatever
   * the store already holds. The caller does the merging, so this function answers the same question
   * for an upload preview and for a pack already in the store without two code paths.
   */
  readonly files: readonly PackFile[];
}

/**
 * Judge one (L2, L1?) pair's pack against what the pipeline requires of it.
 *
 * Pure and total: it never reads the store, the filesystem or the network, so the upload form can
 * run it on every change and the server can run the identical rules before writing.
 */
export const checkLanguagePack = (input: PackCompletenessInput): PackReport => {
  const findings: PackFinding[] = [];
  const byPath = new Map(input.files.map((file) => [file.path, file.content]));
  const l2Dir = `L2_${input.l2}`;
  const l1Dir = input.l1 === undefined ? undefined : `L1_${input.l1}`;

  const requireFile = (path: string, what: string, fix: string): string | undefined => {
    const content = byPath.get(path);
    if (content === undefined) {
      findings.push({ severity: "blocker", path, message: `${what} is missing.`, fix });
      return undefined;
    }
    if (content.trim().length === 0) {
      findings.push({ severity: "blocker", path, message: `${what} is empty.`, fix });
      return undefined;
    }
    return content;
  };

  // ── The registry ────────────────────────────────────────────────────────────
  // Both runtimes resolve `${L2}` / `${L1}` display names through this file and hard-exit pointing
  // at it when a code is absent. A pack whose files are all present but whose code is unregistered
  // fails at the very first step, with an error that reads as a pipeline bug rather than a missing
  // line of YAML.
  const registry = byPath.get("language_codes.yml");
  if (registry === undefined) {
    findings.push({
      severity: "blocker",
      path: "language_codes.yml",
      message: "The language-code registry is missing.",
      fix: "Include `language_codes.yml` in the upload, or upload into a studio that already holds it.",
    });
  } else {
    for (const code of [input.l2, ...(input.l1 === undefined ? [] : [input.l1])]) {
      if (!registryHasCode(registry, code)) {
        findings.push({
          severity: "blocker",
          path: "language_codes.yml",
          message: `The registry has no entry for \`${code}\`.`,
          fix: `Add a line \`${code}: { name: "<display name>" }\` — the upload form's display-name field writes it for you.`,
        });
      }
    }
  }

  // ── The L2 pack ─────────────────────────────────────────────────────────────
  requireFile(
    `${l2Dir}/language_profile.md`,
    "The pack manifest `language_profile.md`",
    "Model it on `L2_QMS/language_profile.md`: display name and code, the register system's actual forms, the A1/A2/B scene model, and a `## Grammar inventory notes` section.",
  );

  const personas = requireFile(
    `${l2Dir}/personas.md`,
    "`personas.md`",
    "Model it on `L2_QMS/personas.md`: `# Recurring user personas`, one `## <FirstName>` block per persona, then `## Learner positioning` and `## Cast distribution rules`.",
  );
  if (personas !== undefined) {
    for (const section of REQUIRED_PERSONA_SECTIONS) {
      if (!personas.includes(section)) {
        findings.push({
          severity: "blocker",
          path: `${l2Dir}/personas.md`,
          message: `The \`${section}\` section is missing.`,
          fix: "Add it with that heading text exactly — `dialog_polish` excludes these two headings from persona parsing BY NAME, so a renamed one is parsed as a persona and fails the cast gate.",
        });
      }
    }
    const cast = castNames(personas);
    if (cast.length === 0) {
      findings.push({
        severity: "blocker",
        path: `${l2Dir}/personas.md`,
        message: "No persona sections found.",
        fix: "Give each persona its own `## <FirstName>` heading — the launch-time cast gate fails an empty cast file outright.",
      });
    } else {
      if (cast.length < 4) {
        findings.push({
          severity: "warning",
          path: `${l2Dir}/personas.md`,
          message: `Only ${cast.length} persona${cast.length === 1 ? "" : "s"} (${cast.join(", ")}); the convention is 4 main characters.`,
          fix: "Add personas until the cast covers mentor, peer-friend, community connector and teacher — a thin cast makes every unit sound like the same speaker.",
        });
      }
      for (const block of blocksOf(personas)) {
        if ((REQUIRED_PERSONA_SECTIONS as readonly string[]).includes(`## ${block.name}`)) continue;
        const absent = PERSONA_FIELDS.filter((field) => !block.body.includes(field));
        if (absent.length > 0) {
          findings.push({
            severity: "warning",
            path: `${l2Dir}/personas.md`,
            message: `Persona \`${block.name}\` is missing ${absent.join(", ")}.`,
            fix: "Fill every field to the depth of the `L2_QMS` cast — named neighbourhoods, concrete dialect features, real lexical items. A structurally valid but generically written persona produces flat dialogue.",
          });
        }
      }
      findings.push({
        severity: "info",
        path: `${l2Dir}/personas.md`,
        message: `Cast names (${cast.join(", ")}) are resolved against \`persona-config\` at launch, not here.`,
        fix: "Each must be a canonical, non-deprecated `main-character` persona. Studio cannot check this — that registry is Python source, not data — so an unknown name fails at launch rather than at upload.",
      });
    }
  }

  requireFile(
    `${l2Dir}/reference_dialogues.md`,
    "`reference_dialogues.md`",
    "Provide the dialogue exemplars `dialog_polish` writes against; model them on `L2_QMS/reference_dialogues.md`.",
  );

  // ── The L1 pack ─────────────────────────────────────────────────────────────
  // Skipped entirely when no L1 is named: an upload that only defines an L2 is a legitimate thing to
  // do (the L1 side is shared across every L2 that audience learns, and usually exists already). The
  // New Curriculum modal asks this question again per PAIR, where the L1 is known.
  if (l1Dir !== undefined) {
    const l1Profile = requireFile(
      `${l1Dir}/profile.md`,
      "The L1 profile",
      "Model it on `L1_QAE/profile.md`: the gloss conventions for this L1, plus the machine-read pronoun-noise marker.",
    );
    if (l1Profile !== undefined && !PRONOUN_NOISE_MARKER.test(l1Profile)) {
      findings.push({
        severity: "warning",
        path: `${l1Dir}/profile.md`,
        message: "The `pronoun-noise` marker is missing.",
        fix: "Add `<!-- pronoun-noise: i, you, he, she, we, they, it -->` with THIS L1's subject pronouns, lowercase. Without it the match-exercise parser silently falls back to the English set.",
      });
    }

    const tutors = requireFile(
      `${l1Dir}/tutors.md`,
      "`tutors.md`",
      "Model it on `L1_QAE/tutors.md`: one `## <FirstName>` block per tutoring voice, each declaring `**Voice / tone:**`.",
    );
    if (tutors !== undefined) {
      const voices = blocksOf(tutors).filter((block) => block.body.includes(TUTOR_VOICE_MARKER));
      if (voices.length === 0) {
        findings.push({
          severity: "blocker",
          path: `${l1Dir}/tutors.md`,
          message: "No tutor sections found.",
          fix: "Give each tutor a `## <FirstName>` heading and a `**Voice / tone:**` line — `validate_tutors_md` keys on that marker and exits when it finds none.",
        });
      }
    }
  }

  // ── Degrade-silently warnings ───────────────────────────────────────────────
  // A file that exists but lists nothing is the same thing to the runtime as no file at all: it
  // reads an empty list and falls back to the Spanish defaults. Checking only for presence would
  // pass the skeleton this validator's own template generates, which is the one case where a
  // reassuring report is most likely to be believed.
  for (const name of LANGUAGE_RULE_FILES) {
    const rules = byPath.get(`${l2Dir}/${name}`);
    const empty =
      rules !== undefined && !rules.split("\n").some((line) => line.trim().startsWith("- "));
    if (rules === undefined || empty) {
      findings.push({
        severity: "warning",
        path: `${l2Dir}/${name}`,
        message: `\`${name}\` ${rules === undefined ? "is absent" : "lists no entries"}.`,
        fix: "The Python runtime falls back to SPANISH defaults for this list rather than failing, so the run will look fine and be wrong. Copy the `L2_ITA` file and replace its entries.",
      });
    }
  }

  // The template ships every file pre-filled with `<<FILL: …>>` markers. Uploading one with markers
  // still in it is the likeliest way to get a structurally valid, semantically empty pack — every
  // heading present, nothing said — so each such file is named rather than passing quietly.
  for (const file of input.files) {
    if (file.content.includes(FILL_MARKER)) {
      const count = file.content.split(FILL_MARKER).length - 1;
      findings.push({
        severity: "warning",
        path: file.path,
        message: `Still has ${count} unfilled \`${FILL_MARKER}…>>\` placeholder${count === 1 ? "" : "s"} from the template.`,
        fix: "Replace each one with real content, to the depth of the `L2_QMS` pack — concrete dialect features, named places, real lexical items. A pack whose placeholders survive generates flat, generic material.",
      });
    }
  }

  const profile = byPath.get(`${l2Dir}/language_profile.md`);
  if (profile !== undefined && !profile.includes("## Grammar inventory notes")) {
    findings.push({
      severity: "warning",
      path: `${l2Dir}/language_profile.md`,
      message: "No `## Grammar inventory notes` section.",
      fix: "Steps 0s/0a read it (verb allowlist, deferrals, example register) when generating this L2's grammar inventory. With no upstream inventory record for this L2, the generated arm is the only route and this section is its only input.",
    });
  }

  findings.push({
    severity: "info",
    message: "The grammar ledger is generated, not uploaded.",
    fix: `Run \`run_pipeline.py --l2 ${input.l2} --l1 ${input.l1 ?? "<L1>"} --cefr A1.1 --step 0\` once per pair × CEFR before the first full run; if \`curriculum_grammars.json\` has no record for this L2, add \`--grammar-source generated\`.`,
  });

  return { l2: input.l2, l1: input.l1, findings, complete: !hasBlocker(findings) };
};

/** Every language code present in a set of pack paths, split by side. */
export const languageCodesOfPaths = (
  paths: readonly string[],
): { readonly l1: readonly string[]; readonly l2: readonly string[] } => {
  const l1 = new Set<string>();
  const l2 = new Set<string>();
  for (const path of paths) {
    const [root] = path.split("/");
    if (root === undefined) continue;
    if (root.startsWith("L1_")) l1.add(root.slice(3));
    if (root.startsWith("L2_")) l2.add(root.slice(3));
  }
  return { l1: [...l1].sort(), l2: [...l2].sort() };
};

/**
 * Normalise a path as a browser hands it over, into a pack-relative path.
 *
 * A directory picker yields `webkitRelativePath` rooted at whatever the human selected: the `packs/`
 * directory, one `L2_FRA/` directory, or a folder with an arbitrary name. All three must land on the
 * same pack-relative path, so this drops every leading segment before the first `L1_`/`L2_` root —
 * or, for the registry, keeps only its filename. Returns `undefined` for a path with no
 * pack-relative interpretation (a stray `.DS_Store`, an image, a README).
 */
export const packRelativePath = (rawPath: string): string | undefined => {
  const segments = rawPath.split("/").filter((segment) => segment.length > 0);
  const rootIndex = segments.findIndex(
    (segment) => segment.startsWith("L1_") || segment.startsWith("L2_"),
  );
  if (rootIndex >= 0) {
    const relative = segments.slice(rootIndex).join("/");
    return relative.endsWith(".md") || relative.endsWith(".yml") ? relative : undefined;
  }
  return segments.at(-1) === "language_codes.yml" ? "language_codes.yml" : undefined;
};

/**
 * The `CODE -> display name` pairs in `language_codes.yml`.
 *
 * Parsed with a line regex rather than a YAML library: the file's own header documents exactly one
 * admissible shape (`<CODE>: { name: "<display name>" }`), studio only ever writes that shape, and
 * pulling a YAML parser into `core` for four lines of flow mapping would be the larger liability.
 * A line that does not match is skipped, so comments and blanks cost nothing.
 */
export const parseLanguageRegistry = (
  content: string,
): readonly { readonly code: string; readonly name: string }[] => {
  const entries: { code: string; name: string }[] = [];
  for (const line of content.split("\n")) {
    const match = /^\s*([A-Za-z0-9_]+)\s*:\s*\{\s*name\s*:\s*["']?([^"'}]+?)["']?\s*\}\s*$/.exec(
      line,
    );
    if (match?.[1] !== undefined && match[2] !== undefined) {
      entries.push({ code: match[1], name: match[2].trim() });
    }
  }
  return entries;
};

/** One language studio knows about, and whether a run may be launched against it. */
export const LanguageReadiness = Schema.Struct({
  code: Schema.String,
  // From the registry when it has an entry, else the code itself — a pack whose files landed but
  // whose registry line did not is exactly the state this surface has to be able to show.
  name: Schema.String,
  // A code may be used as either side, and several are (`QAE` is an L2 pack and the L1 of every
  // pair). Both flags rather than one enum, so the New Curriculum modal can filter each select on
  // the side it is asking about.
  isL1: Schema.Boolean,
  isL2: Schema.Boolean,
  // L2 completeness only — the L2 side of `checkLanguagePack`, with no L1 named. The pair's L1 files
  // are judged again at launch, where the pair is known.
  complete: Schema.Boolean,
  blockers: Schema.Array(Schema.String),
});
export type LanguageReadiness = typeof LanguageReadiness.Type;

/**
 * Fold every pack file studio holds into the per-language answer the "Language taught" select
 * needs: which codes exist, what to call them, and whether each one's L2 pack can actually carry a
 * run.
 *
 * A code appears here if the registry names it OR a pack directory exists for it — deliberately the
 * union, because both halves of that state are wrong in an interesting way. A registry line with no
 * files is a language someone declared and never uploaded; files with no registry line is a pack
 * that will hard-exit at launch. Hiding either would hide the reason the option is unavailable.
 */
export const languageReadiness = (files: readonly PackFile[]): readonly LanguageReadiness[] => {
  const registry = files.find((file) => file.path === "language_codes.yml");
  const names = new Map(
    (registry === undefined ? [] : parseLanguageRegistry(registry.content)).map((entry) => [
      entry.code,
      entry.name,
    ]),
  );
  const { l1, l2 } = languageCodesOfPaths(files.map((file) => file.path));
  const codes = [...new Set([...names.keys(), ...l1, ...l2])].sort();
  return codes.map((code) => {
    const isL2 = l2.includes(code);
    // Only a code that HAS an L2 pack is judged as one. Running the L2 check over an L1-only code
    // (`QAE` is the L1 of every pair and has no `L2_QAE` tree in some deployments) would report it
    // as incomplete for missing L2 files it is not supposed to have — nonsense in the modal, and
    // the kind of nonsense a human learns to ignore.
    const blockers = isL2
      ? checkLanguagePack({ l2: code, files })
          .findings.filter((finding) => finding.severity === "blocker")
          .map((finding) => finding.message)
      : [];
    return {
      code,
      name: names.get(code) ?? code,
      isL1: l1.includes(code),
      isL2,
      complete: isL2 && blockers.length === 0,
      blockers,
    };
  });
};

/** Add or replace one `CODE: { name: "…" }` line in the registry, preserving everything else. */
export const withRegistryEntry = (registry: string, code: string, name: string): string => {
  const entry = `${code}: { name: "${name}" }`;
  const lines = registry.split("\n");
  const index = lines.findIndex((line) => line.trim().startsWith(`${code}:`));
  if (index >= 0) {
    lines[index] = entry;
    return lines.join("\n");
  }
  const trimmed = registry.replace(/\n+$/, "");
  return `${trimmed}\n${entry}\n`;
};

// ── The upload's wire contract ──────────────────────────────────────────────────
// Here rather than in `apps/server` because both ends decode it: the server enforces it at the REST
// boundary, and the upload form renders the report it returns. A second copy in the web app would be
// the same drift these rules exist to prevent.

// A pack is a couple of dozen small markdown files, so both bounds sit far above any honest upload.
// They exist because the whole payload is held in memory and every file is written, and a JSON body
// with no bound at all is a memory-exhaustion risk — the same reasoning as `MAX_IMPORT_BYTES`, and
// deliberately the same number for the per-document cap. Declared here rather than imported from
// `document-create.ts` so the upload's limits read in one place; if they ever need to differ, they
// can, without the import implying they may not.
export const MAX_PACK_FILES = 200;
export const MAX_PACK_FILE_BYTES = 1_048_576;

export const UploadPackFile = Schema.Struct({
  // As the browser hands it over (`webkitRelativePath`), normalised on both sides. The client
  // normalises so it can preview the report; the server normalises again because the client's copy
  // is advice and the server's is enforcement.
  path: Schema.String.pipe(Schema.maxLength(512)),
  content: Schema.String.pipe(Schema.maxLength(MAX_PACK_FILE_BYTES)),
});
export type UploadPackFile = typeof UploadPackFile.Type;

export const UploadLanguagePackArgs = Schema.Struct({
  // The L2 the upload is FOR. Named explicitly rather than inferred from the file paths: an upload
  // may legitimately carry both an L2 and an L1 tree, and the report has to be about one pack.
  //
  // Branded, so a display name typed into the code field ("Mexican Spanish") is refused at the
  // decode boundary rather than creating an `L2_Mexican Spanish/` directory nothing can join on.
  l2: CurriculumLanguage,
  l1: Schema.optional(CurriculumLanguage),
  files: Schema.Array(UploadPackFile).pipe(Schema.maxItems(MAX_PACK_FILES)),
  // `CODE -> display name` for codes this upload introduces; written into `language_codes.yml`.
  displayNames: Schema.optional(Schema.Record({ key: Schema.String, value: Schema.String })),
  // Validate and report, write nothing. The form runs this on every file selection, so a human sees
  // the whole report before anything is durable.
  dryRun: Schema.Boolean,
  // Permit replacing documents the store already holds. Off by default.
  overwrite: Schema.Boolean,
});
export type UploadLanguagePackArgs = typeof UploadLanguagePackArgs.Type;

export const UploadLanguagePackResult = Schema.Struct({
  report: PackReport,
  // What this call actually wrote. Empty on a dry run and on a refusal — the two cases the caller
  // must not confuse with "nothing needed writing".
  written: Schema.Array(Schema.String),
  // True when the pack was written, or would have been on a dry run with no blockers.
  accepted: Schema.Boolean,
});
export type UploadLanguagePackResult = typeof UploadLanguagePackResult.Type;

// ── The template ────────────────────────────────────────────────────────────────
// A fillable skeleton of the pack, generated from the very constants the rules above check, so the
// two cannot drift: a freshly downloaded template uploads with zero blockers by construction, and
// every remaining gap is a warning naming itself.
//
// Deliberately structure and instructions ONLY. The linguistic content — what a persona sounds
// like, which register forms a language has — is knowledge, and knowledge lives in the pack files
// authors write, never in this TypeScript (ADR-0021). The `L2_QMS` pack is the depth reference and
// the template points at it rather than paraphrasing it.

const fill = (what: string): string => `${FILL_MARKER} ${what}>>`;

const personaBlock = (n: number): string =>
  [
    `## ${fill(`first name of persona ${n}`)}`,
    "",
    ...PERSONA_FIELDS.map(
      (field) => `${field.endsWith(":**") ? field : `${field}:**`} ${fill("…")}`,
    ),
    "",
  ].join("\n");

const LANGUAGE_PROFILE = (l2: string): string =>
  [
    `# ${fill("display name of this language")} (${l2})`,
    "",
    `Code: \`${l2}\``,
    "",
    "## Register system",
    "",
    `Write the ACTUAL forms and when each is used — "vous", "Sie", "usted", "-습니다" — never`,
    `"use the appropriate formal register". ${fill("the register forms and their use")}`,
    "",
    "## Scene model",
    "",
    `A1 / A2 / B scenes for this language and culture. ${fill("the scene model")}`,
    "",
    "## Grammar inventory notes",
    "",
    "Read by pipeline steps 0s/0a when generating this L2's grammar inventory: the verb allowlist,",
    "what is deferred to later levels, and the register examples to use.",
    "",
    fill("verb allowlist, deferrals, example register"),
    "",
  ].join("\n");

const PERSONAS = (): string =>
  [
    "# Recurring user personas",
    "",
    "Four main characters is the convention: a mentor, a peer-friend, a community connector and a",
    "teacher. Match the depth of `L2_QMS/personas.md` — named neighbourhoods, concrete dialect",
    "features, real lexical items. A structurally valid but generically written cast produces flat",
    "dialogue, which is the failure hardest to spot after the fact.",
    "",
    "Each name below must be a canonical, non-deprecated `main-character` persona in `persona-config`.",
    "Studio cannot check that — it is resolved at launch — so an invented name fails there, not here.",
    "",
    personaBlock(1),
    personaBlock(2),
    personaBlock(3),
    personaBlock(4),
    REQUIRED_PERSONA_SECTIONS[0],
    "",
    `Who the learner is in these dialogues, and how the cast treats them. ${fill("…")}`,
    "",
    REQUIRED_PERSONA_SECTIONS[1],
    "",
    `How the cast is distributed across units. ${fill("…")}`,
    "",
  ].join("\n");

const REFERENCE_DIALOGUES = (): string =>
  [
    "# Reference dialogues",
    "",
    "The exemplars `dialog_polish` writes against. Model them on `L2_QMS/reference_dialogues.md`:",
    "real exchanges in this language, at the register and pace a learner meets them.",
    "",
    `## ${fill("scene name")}`,
    "",
    fill("the dialogue"),
    "",
  ].join("\n");

const ruleFile = (name: string): string =>
  [
    `# ${name} — one \`- entry\` per line.`,
    "#",
    "# LEAVING THIS EMPTY IS NOT NEUTRAL: the Python runtime falls back to the SPANISH list rather",
    "# than failing, so the run looks fine and is wrong. Copy `L2_ITA/" +
      name +
      "` and replace its",
    "# entries with this language's.",
    "",
    `# - ${fill("first entry")}`,
    "",
  ].join("\n");

const L1_PROFILE = (l1: string): string =>
  [
    `# ${fill("display name of this language")} (${l1}) — L1 profile`,
    "",
    "How glosses are written for speakers of this language.",
    "",
    fill("gloss conventions"),
    "",
    "## Pronoun noise",
    "",
    "Machine-read by the match-exercise parser. Without it the parser silently falls back to the",
    "ENGLISH pronoun set, so this matters for any L1 that is not English. Lowercase, comma-separated.",
    "",
    "<!-- pronoun-noise: i, you, he, she, we, they, it -->",
    "",
  ].join("\n");

const TUTORS = (): string =>
  [
    "# Tutors",
    "",
    "The tutoring voices that explain in the learner's own language. One `## <FirstName>` block each,",
    "every one declaring the marker below — `validate_tutors_md` keys on it and exits when it finds",
    "none. Each name must be a canonical `tutor` persona in `persona-config`.",
    "",
    `## ${fill("tutor first name")}`,
    "",
    `${TUTOR_VOICE_MARKER} ${fill("…")}`,
    "",
  ].join("\n");

/**
 * The skeleton pack for a new language, as `(path, content)` pairs ready to be zipped.
 *
 * The L1 side is included only when an L1 code is given: an L1 pack is shared by every L2 that
 * audience learns and usually exists already, so generating one unasked would invite an author to
 * overwrite a good one with placeholders.
 *
 * `language_codes.yml` is deliberately NOT generated. The registry is shared by every language, and
 * a skeleton copy naming only the new code is exactly the shape that, uploaded, would drop every
 * other language's entry. The upload form writes the registry line from its display-name field
 * instead.
 */
export const templateLanguagePack = (l2: string, l1?: string): readonly PackFile[] => [
  { path: `L2_${l2}/language_profile.md`, content: LANGUAGE_PROFILE(l2) },
  { path: `L2_${l2}/personas.md`, content: PERSONAS() },
  { path: `L2_${l2}/reference_dialogues.md`, content: REFERENCE_DIALOGUES() },
  ...LANGUAGE_RULE_FILES.map((name) => ({
    path: `L2_${l2}/${name}`,
    content: ruleFile(name),
  })),
  ...(l1 === undefined
    ? []
    : [
        { path: `L1_${l1}/profile.md`, content: L1_PROFILE(l1) },
        { path: `L1_${l1}/tutors.md`, content: TUTORS() },
      ]),
];
